/** The reader toolbar (UX-MAP-12): layer toggle and legend, channel focus picker, evidence and flagged-stretch navigation,
 * chapter menu, find box and the shortcuts preference. */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { openShortcuts } from '../components/HelpMenu.tsx';
import { Term } from '../components/Term.tsx';
import { ChannelTerm } from '../components/Term.tsx';
import { FOCUS_CAP, clearFocus, focus, focusReplaced, removeFocus, toggleFocus } from '../focus.ts';
import { READER_EXPLAIN } from '../strings-explain.ts';
import { TOOLBAR } from '../strings-reader.ts';
import { Icon, Notice } from '../ui.tsx';

export type Layer = 'all' | 'focused';

export interface FindState {
	query: string;
	total: number;
	/** Number of ranges held (may be fewer than `total`). */
	held: number;
	current: number;
	pending: boolean;
}

export interface ToolbarProps {
	vm: ViewModel;
	layer: Layer;
	onLayer: (l: Layer) => void;
	onPrevEvidence: () => void;
	onNextEvidence: () => void;
	onPrevFlagged: () => void;
	onNextFlagged: () => void;
	chapter: number;
	onChapter: (i: number) => void;
	position: string;
	shortcuts: boolean;
	onShortcuts: (on: boolean) => void;
	open: boolean;
	onOpen: (open: boolean) => void;
	find: FindState;
	onFind: (query: string) => void;
	onFindStep: (dir: 1 | -1) => void;
}

const STYLE_NAMES = [TOOLBAR.styleSolid, TOOLBAR.styleDashed, TOOLBAR.styleDotted] as const;

function ChannelPicker({ vm }: { vm: ViewModel }) {
	const [open, setOpen] = useState(false);
	const [q, setQ] = useState('');
	const root = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const search = useRef<HTMLInputElement>(null);
	const ids = focus.value;
	useEffect(() => {
		if (!open) return;
		search.current?.focus();
		const away = (e: MouseEvent) => {
			if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', away);
		return () => document.removeEventListener('mousedown', away);
	}, [open]);
	const needle = q.trim().toLowerCase();
	const groups = new Map<string, typeof vm.channels>();
	for (const c of vm.channels) {
		if (needle && !c.id.toLowerCase().includes(needle) && !c.name.toLowerCase().includes(needle))
			continue;
		const l = groups.get(c.family);
		if (l) l.push(c);
		else groups.set(c.family, [c]);
	}
	const onKey = (e: KeyboardEvent) => {
		if (e.key === 'Escape' && open) {
			e.stopPropagation();
			setOpen(false);
			trigger.current?.focus();
		}
	};
	return (
		<div class="rd-picker" ref={root} onKeyDown={onKey}>
			<button
				type="button"
				class="btn btn-secondary rd-picker-btn"
				ref={trigger}
				aria-expanded={open}
				aria-controls="rd-picker-panel"
				data-testid="rd-picker-toggle"
				onClick={() => setOpen(!open)}
			>
				{ids.length ? TOOLBAR.channelsCount(ids.length) : TOOLBAR.channels} <Icon name="chevron" />
			</button>
			{open ? (
				<div
					class="rd-picker-panel"
					id="rd-picker-panel"
					// biome-ignore lint/a11y/useSemanticElements: a popover panel with its own heading-less label, not a form fieldset
					role="group"
					aria-label={TOOLBAR.pickerLabel}
					data-testid="rd-picker"
				>
					<input
						ref={search}
						type="search"
						class="input rd-search"
						aria-label={TOOLBAR.search}
						placeholder={TOOLBAR.searchPlaceholder}
						value={q}
						onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)}
					/>
					<div class="rd-picker-list">
						{groups.size === 0 ? <p class="dim">{TOOLBAR.noMatch}</p> : null}
						{[...groups.entries()].map(([family, list]) => (
							<fieldset class="rd-family" key={family}>
								<legend>{family}</legend>
								{list.map((c) => (
									<label class="rd-opt" key={c.id}>
										<input
											type="checkbox"
											checked={ids.includes(c.id)}
											data-channel={c.id}
											onChange={() => toggleFocus(c.id)}
										/>
										<span class="mono">{c.id}</span>
										<span class="rd-opt-name">{c.name}</span>
									</label>
								))}
							</fieldset>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}

function Legend({ vm, layer }: { vm: ViewModel; layer: Layer }) {
	const ids = focus.value;
	if (layer === 'all')
		return (
			<div class="rd-legend" data-testid="rd-legend-all">
				<span class="rd-lg">
					<span class="rd-lg-line d1" aria-hidden="true" />
					{TOOLBAR.legendAll.one}
				</span>
				<span class="rd-lg">
					<span class="rd-lg-line d2" aria-hidden="true" />
					{TOOLBAR.legendAll.two}
				</span>
				<span class="rd-lg">
					<span class="rd-lg-line d3" aria-hidden="true" />
					{TOOLBAR.legendAll.three}
				</span>
				<span class="rd-lg">
					<span class="rd-lg-line d0" aria-hidden="true" />
					<Term k="weight-zero">{TOOLBAR.legendAll.zero}</Term>
				</span>
			</div>
		);
	return (
		<div class="rd-legend" data-testid="rd-legend-focus">
			{ids.length > 0 ? (
				<button type="button" class="btn-text" onClick={clearFocus}>
					{TOOLBAR.clearFocus}
				</button>
			) : null}
			{ids.map((id, i) => {
				const c = vm.channels.find((x) => x.id === id);
				return (
					<span class="rd-lg" key={id} data-slot={i + 1} data-channel={id}>
						<span class={`rd-lg-line l${i + 1}`} aria-hidden="true" data-style={STYLE_NAMES[i]} />
						<span class="mono">
							<ChannelTerm id={id as ChannelId} name={c?.name} />
						</span>
						<button
							type="button"
							class="rd-x"
							aria-label={TOOLBAR.removeFocus(id)}
							onClick={() => removeFocus(id as ChannelId)}
						>
							×
						</button>
					</span>
				);
			})}
		</div>
	);
}

export function Toolbar(p: ToolbarProps): JSX.Element {
	const ids = focus.value;
	const replaced = focusReplaced.value;
	useEffect(() => {
		if (!replaced) return;
		const t = setTimeout(() => {
			focusReplaced.value = false;
		}, 10000);
		return () => clearTimeout(t);
	}, [replaced]);
	const findTimer = useRef(0);
	const [findText, setFindText] = useState(p.find.query);
	const onFindInput = (v: string) => {
		setFindText(v);
		clearTimeout(findTimer.current);
		findTimer.current = window.setTimeout(() => p.onFind(v), 250);
	};
	const fs = p.find;
	return (
		<div class="rd-toolbar" role="toolbar" aria-label={TOOLBAR.label} data-testid="rd-toolbar">
			<button
				type="button"
				class="btn btn-secondary rd-tools-toggle"
				aria-expanded={p.open}
				aria-controls="rd-tools-body"
				onClick={() => p.onOpen(!p.open)}
			>
				{TOOLBAR.evidenceMenu} <Icon name="chevron" />
			</button>
			<div class={`rd-tools-body${p.open ? ' is-open' : ''}`} id="rd-tools-body">
				<div class="rd-row1">
					{/* biome-ignore lint/a11y/useSemanticElements: a segmented toggle of two buttons, not a form fieldset */}
					<div class="seg" role="group" aria-label={TOOLBAR.layer}>
						<button
							type="button"
							class={`seg-btn${p.layer === 'focused' ? ' is-on' : ''}`}
							aria-pressed={p.layer === 'focused'}
							data-testid="rd-layer-focused"
							onClick={() => p.onLayer('focused')}
						>
							{ids.length ? TOOLBAR.layerFocusedCount(ids.length) : TOOLBAR.layerFocused}
						</button>
						<button
							type="button"
							class={`seg-btn${p.layer === 'all' ? ' is-on' : ''}`}
							aria-pressed={p.layer === 'all'}
							data-testid="rd-layer-all"
							onClick={() => p.onLayer('all')}
						>
							{TOOLBAR.layerAll}
						</button>
					</div>
					<ChannelPicker vm={p.vm} />
					<Legend vm={p.vm} layer={p.layer} />
					<p class="dim rd-layer-help" data-testid="rd-layer-help">
						{p.layer === 'all' ? READER_EXPLAIN.layerAll : READER_EXPLAIN.layerFocused}
					</p>
				</div>
				{replaced ? (
					<div data-testid="rd-focus-notice">
						<Notice kind="info" live>
							{TOOLBAR.focusCap} {READER_EXPLAIN.focusCapWhy}{' '}
							<button
								type="button"
								class="btn-text"
								onClick={() => {
									focusReplaced.value = false;
								}}
							>
								{TOOLBAR.dismiss}
							</button>
						</Notice>
					</div>
				) : null}
				<div class="rd-row2">
					<div class="rd-nav">
						<button
							type="button"
							class="btn-text"
							aria-label={TOOLBAR.prevEvidenceLabel}
							aria-keyshortcuts="Shift+N"
							data-testid="rd-prev-evidence"
							onClick={p.onPrevEvidence}
						>
							{TOOLBAR.prevEvidence}
						</button>
						<button
							type="button"
							class="btn-text"
							aria-label={TOOLBAR.nextEvidenceLabel}
							aria-keyshortcuts="N"
							data-testid="rd-next-evidence"
							onClick={p.onNextEvidence}
						>
							{TOOLBAR.nextEvidence}
						</button>
						<button
							type="button"
							class="btn-text"
							aria-label={TOOLBAR.prevFlaggedLabel}
							aria-keyshortcuts="Shift+H"
							data-testid="rd-prev-flagged"
							onClick={p.onPrevFlagged}
						>
							{TOOLBAR.prevFlagged}
						</button>
						<button
							type="button"
							class="btn-text"
							aria-label={TOOLBAR.nextFlaggedLabel}
							aria-keyshortcuts="H"
							data-testid="rd-next-flagged"
							onClick={p.onNextFlagged}
						>
							{TOOLBAR.nextFlagged}
						</button>
					</div>
					{p.vm.chapters.length > 0 ? (
						<select
							class="input rd-chapter"
							aria-label={TOOLBAR.chapter}
							data-testid="rd-chapter"
							value={p.chapter >= 0 ? String(p.chapter) : ''}
							onChange={(e) => {
								const v = Number((e.currentTarget as HTMLSelectElement).value);
								if (Number.isFinite(v)) p.onChapter(v);
							}}
						>
							{p.chapter < 0 ? <option value="">{TOOLBAR.chapterPlaceholder}</option> : null}
							{p.vm.chapters.map((c, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: the list is fixed per document
								<option key={i} value={String(i)}>
									{TOOLBAR.chapterOption(i + 1, c.title)}
								</option>
							))}
						</select>
					) : null}
					<div class="rd-find-box">
						<input
							type="search"
							class="input rd-find-input"
							aria-label={TOOLBAR.find}
							placeholder={TOOLBAR.findPlaceholder}
							value={findText}
							data-testid="rd-find"
							onInput={(e) => onFindInput((e.currentTarget as HTMLInputElement).value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									p.onFindStep(e.shiftKey ? -1 : 1);
								}
							}}
						/>
						{fs.query ? (
							<>
								<span class="rd-find-count dim" data-testid="rd-find-count" aria-live="polite">
									{fs.total === 0 && !fs.pending
										? TOOLBAR.findNone
										: TOOLBAR.findCount(String(fs.current + 1), String(fs.total))}
								</span>
								<button
									type="button"
									class="btn-text"
									aria-label={TOOLBAR.findPrev}
									onClick={() => p.onFindStep(-1)}
								>
									◂
								</button>
								<button
									type="button"
									class="btn-text"
									aria-label={TOOLBAR.findNext}
									onClick={() => p.onFindStep(1)}
								>
									▸
								</button>
							</>
						) : null}
					</div>
					<span class="rd-pref-group">
						<label class="rd-pref" title={TOOLBAR.shortcutsHelp}>
							<input
								type="checkbox"
								checked={p.shortcuts}
								data-testid="rd-shortcuts"
								onChange={(e) => p.onShortcuts((e.currentTarget as HTMLInputElement).checked)}
							/>
							{TOOLBAR.shortcuts}
						</label>
						<button
							type="button"
							class="btn-text"
							data-testid="rd-shortcuts-open"
							onClick={openShortcuts}
						>
							{READER_EXPLAIN.shortcutsTitle}
						</button>
					</span>
					<span class="rd-pos dim" data-testid="rd-position">
						{p.position}
					</span>
				</div>
			</div>
		</div>
	);
}

export { FOCUS_CAP };
